import { useContext, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ApiContext } from "../../context/ApiContext";
import useStepData from "../../hooks/useStepData";
import apiRequest from "../../requests/apiRequest";
import ErrorModal from "../../shared/ErrorModal";
import Loader from "../../shared/Loader";
import Timeline from "../../shared/Timeline";
import { FormInputs } from "../../types/types";
import __ from "../../utils/getContent";
import getCookie from "../../utils/getCookie";

function resolvePostPaymentRedirect(
	settings: {
		submit_redirect_url?: string;
		submit_redirect_rules?: Array<{
			club_id?: string | number;
			redirect_destination?: string;
		}>;
	} | undefined,
	clubId: string | number | undefined,
): string {
	const defaultUrl =
		(settings?.submit_redirect_url &&
			String(settings.submit_redirect_url).trim()) ||
		"/?form_submitted=1";
	const rules = settings?.submit_redirect_rules ?? [];
	const cid = String(clubId ?? "");
	const matched = Array.isArray(rules)
		? rules.find(
				(row) => String(row.club_id) === cid,
			)
		: undefined;
	const dest = matched?.redirect_destination?.trim();
	return dest || defaultUrl;
}

const StepPayment = (): React.JSX.Element => {
	const {
		getValues,
		formState: { errors }, // eslint-disable-line
	} = useFormContext<FormInputs>();
	const { error, loading } = useStepData(4);
	const { data, setData } = useContext(ApiContext);
	const [submitErrors, setSubmitErrors] = useState<string[]>([]);
	const submittedRef = useRef(false);
	const navigate = useNavigate();

	useEffect(() => {
		//Scroll to top of page.
		window.scrollTo(0, 0);

		// Handle the payment iframe response.
		window.addEventListener(
			"message",
			(event: { origin: string; data: any }) => {
				// Process the message
				if (!submittedRef.current && event?.data?.data?.cardSuccess) {
					submittedRef.current = true;
					setData({
						...data,
						cardSuccess: true,
					});
					doFinalSubmit();
				}
			},
		);
	}, [data, setData]); // eslint-disable-line react-hooks/exhaustive-deps

	const getResponseErrors = (response: any) => {
		let errors = [];

		if (response?.message?.Error) {
			errors.push(response.message.Error);
		}

		const modelStateErrors = response?.message?.ModelState;
		if (modelStateErrors) {
			errors = [...errors, ...Object.values(modelStateErrors)];
		}

		return errors.length ? errors : false;
	};

	const getFormattedSubMembers = () => {
		const subMembers = getValues("submembers") ?? [];

		const membersFormatted = subMembers.flatMap((submember, index) =>
			Object.entries(submember).map(([key, value]) => ({
				[`submembers[${index}][${key}]`]: value,
			})),
		);

		return membersFormatted;
	};

	const getFormattedPackageIDs = () => {
		const packageIDs = data?.startstrongpackageids ?? [];

		const packageIDsFormatted = packageIDs.map(
			(packageID: any, index: any) => ({
				[`startstrongpackageids[${index}]`]: packageID,
			}),
		);

		return packageIDsFormatted ?? [];
	};


	const doFinalSubmit = async () => {
		const requestBody = [
			{ membernumber: data?.MemberNumber },
			{ clubId: data?.get_club_id },
			{ membershiptypeid: data?.selectedMembership?.MembershipTypeId },
			{ language: data?.language ?? "en" },
			{ googleClickId: getCookie("_ga") },
			{ contactId: data?.contact_id },
			{ applysplitifee: data?.splitPayment ? "true" : "false" },
			{ startstrongpackageids: data?.startstrongpackageids },
			{ referral: data?.referralName },
			{ firstname: data?.firstname },
			{ lastname: data?.lastname },
			{ email: data?.email },
			{ username: data?.username },
			{ password: data?.password },
			{ dateOfBirth: data?.dateOfBirth },
			{ gender: data?.gender },
			{ mobilenumber: data?.mobilenumber },
			{ emergencycontact: data?.emergencycontact },
			{ emergencynumber: data?.emergencynumber },
			{ guardianSignature: data?.gardianSignature },
			{ address1: data?.address1 },
			{ address2: data?.address2 },
			{ csizipid: data?.zipId },
			...getFormattedSubMembers(),
			...getFormattedPackageIDs(),
		];

		// NOTE: this endpoint returns an empty 200 response if successful.
		const response = await apiRequest("finalizeMember", requestBody);
		const errors = getResponseErrors(response) as string[];

		if (errors) {
			submittedRef.current = false; // Reset the submitted state for future submissions
			setSubmitErrors(errors);
		} else {
			const resolved = resolvePostPaymentRedirect(
				(window as any).midtownMembershipsSettings,
				data?.get_club_id,
			);
			window.location.href = resolved;
		}
	};

	if (loading) {
		return (
			<div className="flex min-h-[400px] items-center justify-center">
				<Loader colorCircle="#000" colorRotator="#ddd" />
			</div>
		);
	}

	if (error) {
		navigate("?step=contact&error=1");
	}

	return (
		<>
			<Timeline currentStep={5} />

			<ErrorModal
				errors={submitErrors.flat()}
				title={__("submission_errors_title", "Form Error")}
				closeModal={() => {
					setSubmitErrors([]);
				}}
			/>

			<div className="flex flex-col items-center">
				<h1 className="mb-1 text-3xl font-bold uppercase">
					{__("heading_payment_info", "Payment Info")}
				</h1>

				{data?.selectedClub?.Name && (
					<h2 className="location-name mb-2 text-lg font-bold">
						{data.selectedClub.Name}
					</h2>
				)}

				<p className="mb-6 text-xs">
					{__("required_fields_instructions", "Required Fields")}
				</p>

				<div className="w-full max-w-96 border border-gray-400 py-8">
					<iframe
						title={__("payment_gateway", "Payment Method")}
						width="97%"
						height="1300"
						src={data.PaymentGatewayURL}
					/>
				</div>
			</div>
		</>
	);
};

export default StepPayment;
